import React from 'react';
import ConversationBubble from './ConversationBubble';
import IntentChatComposer from './IntentChatComposer';
import { useConversation } from '../../../../contexts/ConversationContext';
import { pendingForSpine } from '../../../../contexts/pendingReplies';
import type { SpineChatPanelData } from '../types';

/** Source Spines SP-3a — the composite intent-chat conversation panel.
 *  A dumb renderer: the processor pre-builds `data.thread` (ordered user +
 *  foreman turns); this panel reuses ConversationContext.sendSpineReply +
 *  pendingReplies for live send/pending state (never a parallel pending path).
 *  Optimistic operator echoes (metadata.source==='spine_reply') show until the
 *  TURN_ADDED reload folds them into the canonical thread (deduped by id). RF
 *  discipline: explicit width, `nowheel` scroll container, composer guards. */
const SpineChatPanelNode: React.FC<{ data: SpineChatPanelData }> = ({ data }) => {
  const { sendSpineReply, pendingReplies, state } = useConversation();
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Optimistic operator echoes for THIS spine, deduped against the canonical
  // thread by messageId (the reload appends the real turn under the same id).
  const threadIds = new Set(data.thread.map((t) => t.messageId));
  const optimistic = (state.messages ?? []).filter(
    (m: any) => m.metadata?.source === 'spine_reply' && !threadIds.has(m.id),
  );
  const pendings = pendingForSpine(pendingReplies, data.spineRootMessageId);

  // Auto-scroll to the latest (inner scrollTop only — never the canvas).
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [data.thread.length, optimistic.length, pendings.length]);

  return (
    <div
      style={{ width: 340 }}
      className="rounded-md border border-amber-500/60 bg-gray-800 flex flex-col overflow-hidden"
    >
      {/* SP-3b — cold-start header (artifact spines only; gated on artifactName
          so blueprint panels are unchanged). Orients the operator before any turn. */}
      {data.artifactName && (
        <div className="px-3 py-2 border-b border-gray-700 text-[12px] text-gray-200 flex items-center gap-2">
          <span className="font-semibold truncate">{data.artifactName}</span>
          {data.hostPersonaName && (
            <span className="px-1.5 py-0.5 rounded-sm bg-emerald-700/60 text-emerald-100 text-[10px] flex-shrink-0">
              {data.hostPersonaName}
            </span>
          )}
        </div>
      )}
      <div
        ref={scrollRef}
        data-testid="intent-panel-scroll"
        className="nowheel overflow-y-auto max-h-[360px] p-1 flex flex-col gap-1"
      >
        {data.thread.length === 0 && (
          <div className="text-[11px] italic text-gray-400 px-2 py-3 text-center">
            Ask {data.hostPersonaName ?? 'the host'} about this {data.artifactType ?? 'artifact'}.
          </div>
        )}
        {data.thread.map((t, i) => (
          <ConversationBubble
            key={t.messageId}
            role={t.role}
            content={t.content}
            personaId={t.personaId}
            index={i}
          />
        ))}
        {optimistic.map((m: any) => (
          <ConversationBubble key={m.id} role="user" content={m.content} personaId={null} />
        ))}
        {pendings.map((p) => (
          <div
            key={p.envelopeMessageId}
            data-testid="pending-bubble"
            className="text-[11px] italic text-gray-400 px-2 py-1"
          >
            {(p.residentName ?? data.hostPersonaName ?? 'The host')} is responding…
          </div>
        ))}
      </div>
      <IntentChatComposer
        onSend={(txt) => sendSpineReply(txt, data.spineRootMessageId, data.residentNamesByPersona)}
      />
    </div>
  );
};

export default SpineChatPanelNode;
